import {
  formatEur,
  isSelfServePlan,
  type PlanId,
  type TermId,
} from "@/shared/config/term-pricing";

const CENTS_PER_EURO = 100;
const PERCENT = 100;

export type PlanPriceRow = {
  plan: string;
  cardPrices: {
    term: string;
    monthlyAmountCents?: number | null;
  }[];
  terms: {
    term: string;
    monthlyAmountCents?: number | null;
    invoiceAmountCents?: number | null;
  }[];
};

export type PlanPrices = {
  monthlyPrice: (plan: PlanId, term: TermId) => number | null;
  annualInvoice: (plan: PlanId, term: TermId) => number | null;
  cardPrice: (plan: PlanId, term: TermId) => number | null;
  savingsVsMonthly: (plan: PlanId, term: TermId) => number;
};

const toEuros = (cents: number | null | undefined): number | null =>
  cents == null ? null : cents / CENTS_PER_EURO;

export const formatPrice = (amount: number | null, locale: string): string =>
  amount === null ? "—" : formatEur(amount, locale);

export const createPlanPrices = (rows: PlanPriceRow[]): PlanPrices => {
  const termRow = (plan: PlanId, term: TermId) =>
    rows.find((row) => row.plan === plan)?.terms.find((t) => t.term === term);

  const displayedCard = (plan: PlanId, term: TermId) =>
    rows
      .find((row) => row.plan === plan)
      ?.cardPrices.find(
        (card) => card.term === (isSelfServePlan(plan) ? term : "monthly"),
      );

  const monthlyPrice = (plan: PlanId, term: TermId) =>
    toEuros(termRow(plan, term)?.monthlyAmountCents);

  const annualInvoice = (plan: PlanId, term: TermId) =>
    toEuros(termRow(plan, term)?.invoiceAmountCents);

  const cardPrice = (plan: PlanId, term: TermId) =>
    toEuros(displayedCard(plan, term)?.monthlyAmountCents);

  return {
    monthlyPrice,
    annualInvoice,
    cardPrice,
    savingsVsMonthly: (plan, term) => {
      const monthly = cardPrice(plan, "monthly");
      const price = cardPrice(plan, term);
      if (monthly === null || price === null || term === "monthly") {
        return 0;
      }
      return Math.max(0, Math.round(((monthly - price) / monthly) * PERCENT));
    },
  };
};
